import React from 'react';
import Link from 'next/link';
import { SnowUnit } from '@/components/blocks/Unit';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { IMostReportedSnowfallItem } from '@/api/home/types';
import { IResortPathInfo } from '@/api/resort/types';
import { Routes } from '@/routes';
import { Heading } from '@/components/ui/heading';

const headerClass = 'mb-4 flex flex-wrap items-end justify-between gap-3';

const titleClass = 'font-heading text-xl text-secondary md:text-2xl';

const viewAllClass = 'text-sm font-semibold text-primary hover:underline';

const SnowfallItem = ({ resort }: { resort: IMostReportedSnowfallItem }) => {
  const href = Routes.resortSnowReport(resort as unknown as IResortPathInfo);

  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border py-3 last:border-b-0">
      <h3 className="m-0 min-w-0 flex-1">
        <Link
          href={href}
          className="text-base font-medium text-foreground hover:text-primary hover:underline"
        >
          {resort.title}
        </Link>
      </h3>
      <span className="shrink-0 text-lg font-semibold tabular-nums text-secondary md:text-xl">
        <SnowUnit values={resort.snow?.last24 ?? undefined} />
      </span>
    </div>
  );
};

type MostReportedSnowfallProps = {
  items?: Array<IMostReportedSnowfallItem> | null;
};

const MostReportedSnowfall = ({ items }: MostReportedSnowfallProps) => {
  const { t } = useOTSTranslation('common');

  if (!items || items.length === 0) {
    return null;
  }

  const itemsToShow = items.filter(({ snow }) => (snow?.last24 ?? 0) > 0).slice(0, 7);

  if (itemsToShow.length === 0) {
    return null;
  }

  return (
    <section>
      <div className={headerClass}>
        <Heading level={2} className={titleClass}>
          {t('common.mostReportedSnow')}
        </Heading>
        <Link href={Routes.snowReports()} className={viewAllClass}>
          {t('common.loadMore')}
        </Link>
      </div>
      <div>
        {itemsToShow.map((resort) => (
          <SnowfallItem key={resort.uuid} resort={resort} />
        ))}
      </div>
    </section>
  );
};

export default MostReportedSnowfall;
